import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  model,
  output,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { ChatStateStoreService } from '../../chat-state-store/chat-state-store.service';
import { filter, fromEvent, map, Observable, tap } from 'rxjs';
import { IChatOption } from '../../chat.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-chat-options',
  templateUrl: './chat-options.component.html',
  styleUrl: './chat-options.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatOptionsComponent implements AfterViewInit {
  private readonly _chatStateStoreService = inject(ChatStateStoreService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _chatOptionsContainer_ = viewChild<ElementRef<HTMLDivElement>>('chatOptionsContainer');
  private readonly _displayedChatOptions_ = viewChildren<ElementRef<HTMLDivElement>>('chatOption');
  private readonly _selection_ = signal<IChatOption>(null);

  protected readonly isSelectionMade_ = computed(() => !!this._selection_());

  readonly chatOptions_ = model.required<IChatOption[]>();
  readonly promptText_ = model.required();
  readonly topicSelected = output<{ key: string, isSubTopic: boolean }>();
  readonly teamSelected = output<{ key: string, name: string }>();

  ngAfterViewInit() {
    this._handleSelections().subscribe();
  }

  private _handleSelections(): Observable<HTMLElement> {
    return fromEvent(this._chatOptionsContainer_().nativeElement, 'click')
      .pipe(
        filter((event) => (event.target['id'] as string).includes('chat-option')),
        map((event) => event.target as HTMLElement),
        tap((target) => {
          // remove selection css class from all chat options
          this._removeSelectionStyles();

          // add selection css class to user selection
          document.getElementById(target.id).classList.add('selection');

          // track selection
          const selection = this.chatOptions_().find((option) => option.name === target.textContent.trim());

          this._selection_.set(selection);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
  }

  private _removeSelectionStyles(): void {
    this._displayedChatOptions_().forEach(option => {
      option.nativeElement.classList.remove('selection');
    });
  }

  protected goToNextStep(): void {
    const { name, key, isSubTopic } = this._selection_();

    if (this._selection_()?.isTeamSelection) {
      this._chatStateStoreService.updatePartialChatState({ subject: name });

      this.teamSelected.emit({ key, name });

      return;
    }

    this._chatStateStoreService.updatePartialChatState({ chatTopic: name });

    this.topicSelected.emit({ key, isSubTopic });
  }
}
